import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Link as RouterLink } from 'react-router-dom';

import { useAnalytics } from 'hooks';
import {
  FadeIn as UIFadeIn,
  HugggID as UIHugggID,
  Link as UILink
} from 'components/ui';
import { Legal as MiscLegal } from 'components/misc';

import { getBasePathname, isCoopVoucher } from 'utilities/huggg';
import styles from './footer.module.scss';
import dictionary from 'config/dictionary';
import { useThemeContext } from '@huggg/react-components';
import PrepaidLegal from 'components/misc/PrepaidLegal/PrepaidLegal';

const Footer = props => {
  const { className, huggg, hasPrepaidCardVoucher, showDetails } = props;
  const { isWelfare } = useThemeContext();
  const analytics = useAnalytics();
  const onTermsClick = useCallback(() => {
    analytics.buttonClickFooterTerms();
  }, [analytics]);

  const isCoop = isCoopVoucher(huggg);

  /**
   * Links to display in the footer. eslint moans if we don't add a key prop,
   * but we don't actually need a key prop. Silly eslint.
   */
  const links = [
    <UILink.HugggHome className={styles.linkListLink} key={null} />,
    huggg.id && (
      <RouterLink
        className={styles.linkListLink}
        key={null}
        onClick={onTermsClick}
        to={`${getBasePathname(huggg)}/partner-terms`}
      >
        {isCoop ? dictionary.footer.links.terms : dictionary.footer.links.partnerTerms}
      </RouterLink>
    ),
    <UILink.HugggPrivacy className={styles.linkListLink} key={null} />,
    <UILink.HugggHelp className={styles.linkListLink} key={null} isWelfare={isWelfare} />
  ].filter(_ => _);

  return (
    <footer
      className={classnames(styles.footer, className)}
      data-test-id="footer"
    >
      <div className="container">
        <UIFadeIn mountOnEnter={true} isVisible={showDetails}>
          <nav
            className={classnames(styles.footerNav, 'u-font-smallest')}
            data-test-id="footer-navigation"
          >
            <ul className={styles.linkList}>
              {links.map((link, index) => (
                <li className={styles.linkListItem} key={index}>
                  {link}
                </li>
              ))}
            </ul>
          </nav>
        </UIFadeIn>
      </div>

      <UIFadeIn mountOnEnter={true} isVisible={showDetails}>
        <div className="container container--wide">
          {hasPrepaidCardVoucher && (<PrepaidLegal className={classnames(styles.legal, 'u-font-smallest')} />)}
          <MiscLegal className={classnames(styles.legal, 'u-font-smallest')} />
          <UIHugggID className={styles.hugggID} id={huggg.id} />
        </div>
      </UIFadeIn>
    </footer>
  );
};

Footer.propTypes = {
  huggg: PropTypes.object.isRequired,
  hasPrepaidCardVoucher: PropTypes.bool,
  className: PropTypes.string,
  showDetails: PropTypes.bool
};

export default Footer;
